import { dataProvider } from '@/services/data-provider';
import { tryCatch } from '@/services/api-client';

import {
  GetConversationsParams,
  CreateConversationBody,
  CreateConversationChannelBody,
  Conversation,
  CreateConversationHandoverBody,
  UpdateConversationBody,
  IConversation,
} from './conversations.types';
import { ConversationHandover } from '../messages/messages.types';

export const getConversations = async ({
  filter,
  sort,
  order,
  perPage,
  page,
}: GetConversationsParams) => {
  const { data, error } = await tryCatch(
    dataProvider.getList<IConversation>('conversations/enriched', {
      pagination: { page: page || 1, perPage: perPage || 5 },
      sort: { field: sort || 'updated_at', order: order || 'DESC' },
      filter,
    }),
  );

  if (error) {
    throw error;
  }

  // Ensure we always return an array, even if the API returns null/undefined
  return {
    data: data || [],
  };
};

export const createConversation = async (
  data: Partial<CreateConversationBody>,
) => {
  const { data: response, error } = await tryCatch(
    dataProvider.create<Conversation>('conversations', { data }),
  );

  if (error) {
    throw error;
  }

  return response;
};

export const updateConversation = async ({
  id,
  data,
  previousData,
}: {
  id: string;
  data: UpdateConversationBody;
  previousData: Conversation;
}) => {
  const { data: response, error } = await tryCatch(
    dataProvider.update<Conversation>('conversations', {
      id,
      data,
      previousData,
    }),
  );

  if (error) {
    throw error;
  }

  return response;
};

export const makeConversationRead = async ({
  id,
  data,
  previousData,
}: {
  id: string;
  data: UpdateConversationBody;
  previousData: Conversation;
}) => {
  const { data: response, error } = await tryCatch(
    dataProvider.update<Conversation>('conversations/mark-messages-read', {
      id,
      data,
      previousData,
    }),
  );

  if (error) {
    throw error;
  }

  return response;
};

export const getConversationsList = async ({
  filter,
  sort,
  order,
  perPage,
  page,
}: GetConversationsParams) => {
  const { data: response, error } = await tryCatch(
    dataProvider.getList<Conversation>('conversations', {
      pagination: { page: page || 1, perPage: perPage || 10 },
      sort: { field: sort || 'updated_at', order: order || 'DESC' },
      filter,
    }),
  );

  if (error) {
    throw error;
  }

  return response;
};

export const getConversation = async ({ id }: { id: string }) => {
  const { data: response, error } = await tryCatch(
    dataProvider.getOne<Conversation>('conversations', { id }),
  );

  if (error) {
    throw error;
  }

  return response;
};

export const createConversationHandover = async (
  data: CreateConversationHandoverBody,
) => {
  const { data: response, error } = await tryCatch(
    dataProvider.create('conversation-handovers', { data }),
  );

  if (error) {
    throw error;
  }

  return response;
};

export const getConversationHandoversList = async ({
  filter,
  sort,
  order,
  perPage,
  page,
}: GetConversationsParams) => {
  const { data: response, error } = await tryCatch(
    dataProvider.getList<ConversationHandover>('conversation-handovers', {
      pagination: { page: page || 1, perPage: perPage || 10 },
      sort: { field: sort || 'updated_at', order: order || 'DESC' },
      filter,
    }),
  );

  if (error) {
    throw error;
  }

  return response;
};

export const createConversationChannel = async (
  data: CreateConversationChannelBody,
) => {
  const { data: response, error } = await tryCatch(
    dataProvider.create('conversations-channels', { data }),
  );

  if (error) {
    throw error;
  }

  return response;
};

export const getConversationChannelsList = async ({
  filter,
  sort,
  order,
  perPage,
  page,
}: GetConversationsParams) => {
  const { data: response, error } = await tryCatch(
    dataProvider.getList('conversations-channels', {
      pagination: { page: page || 1, perPage: perPage || 10 },
      sort: { field: sort || 'updated_at', order: order || 'DESC' },
      filter,
    }),
  );

  if (error) {
    throw error;
  }

  return response;
};

export const deleteConversation = async ({ id }: { id: string }) => {
  const { data: response, error } = await tryCatch(
    dataProvider.delete<Conversation>('conversations', { id }),
  );

  if (error) {
    throw error;
  }

  return response;
};
